import React, { useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import PageHeader from "../../../components/layout/PageHeader";
import { fromWayIn, wayInState } from "../../../components/layout/wayInShell";
import {
  ArrowLeftIcon,
  Button,
  EmptyState,
  failedEmptyState,
  useIsMobile,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import {
  BILLING_PLANS_PATH,
  BILLING_SETTINGS_PATH,
  ROUTES,
  billingResultPath,
} from "../../../routes/paths";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { useWorkspaceReferral } from "../../auth/hooks/useWorkspaceReferral";
import { wayInPending } from "../../auth/services/signupArrival";
import { settingsCrumbs } from "../../settings/settingsTabs";
import AvailableCouponsModal from "../components/AvailableCouponsModal";
import BillingDetailsForm, {
  controlId,
} from "../components/BillingDetailsForm";
import BillingDetailsSummary from "../components/BillingDetailsSummary";
import CheckoutSkeleton from "../components/CheckoutSkeleton";
import CouponField from "../components/CouponField";
import OrderSummary, { purchaseSavings } from "../components/OrderSummary";
import PaymentMethods from "../components/PaymentMethods";
import PinnedCtaBar from "../components/PinnedCtaBar";
import TermChangeBand from "../components/TermChangeBand";
import { stashResult } from "../checkoutOutcome";
import { formatMoney, planTermLabel } from "../pricing";
import { termChangeView } from "../termChange";
import { useRazorpayCheckout } from "../hooks/useRazorpayCheckout";
import { useBillingForm } from "../hooks/useBillingForm";
import { useCoupons } from "../hooks/useCoupons";
import { usePlans } from "../hooks/usePlans";
import { useQuote } from "../hooks/useQuote";
import {
  createPrepaidOrder,
  verifyPrepaidOrder,
} from "../services/prepaidService";
import { toInternationalNumber } from "../../../lib/fieldFormat";

/**
 * Buying a plan: who it is billed to on the left, what it costs on the right,
 * and one button.
 *
 * The whole payment step belongs to the gateway. There is no method to pick
 * here, no card to type, no OTP to wait for — pressing Pay now creates the
 * subscription on our backend and hands it to Razorpay's own window, which
 * draws every one of those itself. See `useRazorpayCheckout`, which is also
 * where the reason lives: no card field on this origin is what keeps the PCI
 * obligation at its smallest.
 *
 * Which plan is in the URL — `/billing/yearly` — rather than in router state,
 * so the page survives a reload and can be linked to. That is a departure from
 * the rule the rest of `paths` follows, and a deliberate one: the param names a
 * product cycle, not a record, so there is nothing to enumerate and nothing
 * about a person in it. A cycle the price list has never heard of is sent back
 * to the plans rather than shown a broken checkout.
 *
 * The form opens on the billing profile the workspace has already saved, and on
 * `My brand` where it has none — a dietitian who has set their practice up has
 * answered most of this once, and should not have to again to buy. What is
 * typed here is written back to the profile on the way to the payment, so the
 * second purchase asks for nothing.
 *
 * Every figure on the right comes from `POST /billing/quote/`, and the total
 * under Pay now is the total the backend would charge. See `quoteService` for
 * why none of it is worked out here.
 *
 * A coupon is typed into the row above the summary, or picked out of the dialog
 * behind "View coupons". Neither of them decides anything: both send the code
 * up with a fresh quote and the figures redraw from the answer, and the code
 * that comes back is the one carried into the purchase — see `useQuote`, which
 * is the only thing on this screen that knows what coupon is on the order.
 */
const BillingCheckoutPage: React.FC = () => {
  const { cycle } = useParams<{ cycle: string }>();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const getToken = useTokenGetter();
  const razorpay = useRazorpayCheckout();
  // The checkout is laid out twice rather than restyled: on a phone the order
  // of the page is different, not just its widths — see the two branches at
  // the foot of this file.
  const isMobile = useIsMobile();

  /**
   * Whether this checkout is a step of a brand new practice's way in rather
   * than a purchase made from inside the app.
   *
   * It decides one thing: whether the row above draws the way into the account
   * menu. This is the one screen of the sign-up flow that is also a screen of
   * the app - the trial offer, its detail step and both confirmations are
   * outside `AppLayout` and have no menu to draw - and somebody who has just
   * signed up reaches it without having seen the dashboard, the rail or the
   * tab bar. Handing them the account menu there offers a way out of the one
   * job the screen is for, into an app they have not arrived in yet.
   *
   * Read once per render rather than held in state: it is settled before this
   * page mounts and `markWayInFinished` is only reached from the result screen
   * after this one is gone. An established practice buying another term
   * answers false, and keeps the menu it has had all along. See
   * `signupArrival`.
   *
   * Two sources, and the shell this screen is drawn in reads the same pair —
   * see `wayInShell`. The session knows a visit that signed up, which is the
   * common way to be here and not the only one: somebody who left the offer
   * and signed in again days later is sent straight back to it with nothing
   * recorded against this visit, and the mark the offer puts on its own
   * navigation is what answers for them.
   */
  const signingUp = wayInPending() || fromWayIn(location.state);

  // What is on sale, off the backend's own price list — the same call the cards
  // this page was opened from drew themselves with, so what is charged here is
  // what was advertised there. Nothing on this screen is priced until it lands.
  const priceList = usePlans();
  const plan = priceList.plans.find((candidate) => candidate.id === cycle);

  // Who this workspace is invoiced, filled from the saved billing profile and
  // the brand behind it, with everything wrong with it as it is typed — see
  // `useBillingForm`, which the trial's detail step shares. Called before the
  // early return below can be reached — hooks cannot be skipped — which costs
  // two fetches on a URL naming a plan that does not exist, and buys a
  // component whose hook order never changes.
  const form = useBillingForm();

  // What the order comes to, off the backend. Asked as soon as there is a plan
  // code to ask about — see `useQuote`.
  const quote = useQuote(plan?.code, plan?.id);
  const price = quote.price;
  // The server's whole answer, which is what the order is created from — see
  // `useQuote`. `price` beside it is the same figures in the shape the summary
  // draws.
  const priced = quote.quote;
  /**
   * The term this purchase replaces or queues behind, where there is one.
   *
   * Read off the quote and off nothing else. Which kind of move it is — a
   * longer term that starts today, or a shorter one that waits for the
   * running one to finish — is the backend's judgement, classified by tier and
   * length against the term actually running and answered as `purchase_kind`
   * on the same call that priced the order. So this screen never works it out
   * from the plan in its own URL, and never from the word on the button that
   * opened it. A reader who opens the year's checkout the day after their year
   * started is quoted a renewal, and the page says so by drawing none of this.
   *
   * `null` on every ordinary purchase, which is nearly all of them. See
   * `termChangeView`.
   */
  const change = termChangeView(priced, priceList.plans);

  // The loads this page waits on, as one wait: the plan it is pricing, what
  // that comes to, and the details it fills itself with. A checkout that
  // appeared a piece at a time would read as a page that broke and then
  // repaired itself — see `CheckoutSkeleton`. Answers that arrive quickly open
  // the page straight away; the shape only stands in once the wait is long
  // enough to notice.
  const waiting =
    form.loading ||
    priceList.status === "loading" ||
    quote.status === "loading";
  const measured = useLoadPhase(waiting);
  /**
   * The placeholders go up at once on the way in, rather than after the delay
   * every other screen in the app waits out.
   *
   * That delay is right for a screen reached from inside the app: it is opened
   * over a page that is still there for the moment the answer takes, and a
   * shimmer that comes and goes inside 300ms reads as a flicker. This screen is
   * not opened over anything — the offer it was opened from is outside the
   * shell, so there is a white page and a push, and what slides in under the
   * delay is nothing at all. The shape of the checkout is the better thing to
   * arrive with, and the reader has just pressed a button that promised it.
   */
  const phase = signingUp && waiting ? "slow" : measured;

  const [paying, setPaying] = useState(false);
  /**
   * Whether the billing details are being asked for rather than read back.
   *
   * Only ever turned on, and only by the reader: a checkout that opens on a
   * complete record shows it as a card with one way into the form, and once
   * that is open it stays open for the life of the page. Where the record is
   * short of anything an invoice needs, there is nothing to read back and the
   * form is what opens — see `form.openedComplete`.
   *
   * The point of the pair is the second purchase. A practice that has bought
   * before has answered these eight questions already; opening them on a
   * screenful of inputs asks them to read the lot to find out that none of it
   * needs touching, and leaves eight fields a stray keystroke can change on
   * the way past the one button that takes their money.
   */
  const [editingDetails, setEditingDetails] = useState(false);
  /** Showing the record rather than asking for it — see `editingDetails`. */
  const readingBackDetails = form.openedComplete && !editingDetails;
  const [browsingCoupons, setBrowsingCoupons] = useState(false);
  // Whose link this practice arrived through, where it arrived through one.
  // Read rather than remembered: the code was spent at sign-up and lives on
  // the workspace from then on.
  const referral = useWorkspaceReferral();

  // Fetched only once the dialog is actually opened: a list nobody has asked
  // to see is a request on every checkout for a panel most buyers never open,
  // and the lookup behind it is throttled per IP.
  //
  // The referral code goes with it where the practice came through a partner's
  // link — without it the lookup can only answer Naurist's own coupons, and
  // the partner's would be missing from the one checkout it was meant for. See
  // `useWorkspaceReferral`.
  // The price list goes with it so a coupon that names the plans it covers can
  // be read as the terms they are — see `useCoupons`.
  const coupons = useCoupons(
    browsingCoupons,
    referral.referralCode,
    priceList.plans
  );

  // A cycle no plan answers to — a URL typed by hand, or a cycle that has
  // stopped being sold. Sent back to the plans rather than shown an empty
  // checkout: there is nothing on this screen without a plan, and the page that
  // has one is a click away. Only once the list is actually known, since until
  // then every cycle looks like one nothing answers to.
  if (priceList.status === "ready" && !plan) {
    return <Navigate to={BILLING_SETTINGS_PATH} replace />;
  }

  // No plan, or no price for it: still on their way, or they never came.
  // Nothing of the checkout is drawn either way — a form somebody can fill in
  // and press Pay on, above a price that could not be loaded, is the one
  // version of this screen that must not exist.
  if (!plan || !price) {
    const failed = priceList.status === "failed" || quote.status === "failed";

    return (
      <div className="p-page pt-page-top">
        {/* Without the plan the trail cannot name what is being bought, so it
            stops at the tab — the crumbs still lead back out, which is the
            whole of what they are for here.

            Hidden on a phone while the page is still arriving, the same way
            the loaded checkout hides it: a title that is drawn over the
            skeleton and then taken away as the figures land is the page
            moving under somebody for no reason. A failure keeps it, because
            then the crumbs are the only way off this screen — except on the
            way in, where there are none to draw and the empty state below
            carries the way back itself. See `signingUp`. */}
        <div className={failed ? undefined : "max-md:hidden"}>
          <PageHeader
            title="Complete your subscription"
            breadcrumbs={signingUp ? undefined : settingsCrumbs("billings")}
          />
        </div>

        {failed ? (
          <EmptyState
            {...failedEmptyState(
              // One message for both, because they are one thing to the buyer:
              // the plan and what it costs are not two facts they asked for
              // separately. Which of the two calls failed is in the console.
              "We could not price this plan",
              <div className="flex items-center justify-center gap-3">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={
                    priceList.status === "failed"
                      ? priceList.reload
                      : quote.reload
                  }
                >
                  Try again
                </Button>

                {/* The way off this screen, for the one reader who has no
                    other. Inside the app the trail above leads back out and
                    the rail is beside it; on the way in there is neither —
                    nothing of the app is drawn around a step of it — and
                    retrying a call that is not answering would be the whole
                    of what this page offered. It goes where Edit goes, which
                    on the way in is the offer. See `handleEdit`. */}
                {signingUp && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => navigate(ROUTES.trial)}
                  >
                    Back to plans
                  </Button>
                )}
              </div>,
            )}
          />
        ) : (
          <>
            <p role="status" className="sr-only">
              Loading this plan…
            </p>
            <div className="mt-4 3xl:mt-6 grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_400px] 3xl:gap-6 4xl:lg:grid-cols-[minmax(0,1fr)_460px]">
              <CheckoutSkeleton phase={phase} />
            </div>
          </>
        )}
      </div>
    );
  }

  /** Ends the checkout on the result route, which confirms it against the
   *  backend before believing any of it. */
  const settle = (
    outcome: Parameters<typeof stashResult>[1],
    extra: Parameters<typeof stashResult>[2] = { subscriptionId: null },
  ) => {
    stashResult(plan.id, outcome, extra);
    // Where they came from goes with them: the confirmation is the third
    // screen of the same flow, and the shell has to know that on a reload of
    // it as well as on the way in. See `wayInShell`.
    navigate(billingResultPath(plan.id), {
      state: signingUp ? wayInState() : undefined,
    });
  };

  /**
   * The payment window was closed without finishing.
   *
   * Deliberately not a trip to the result screen. Nothing was charged, nothing
   * was decided, and the commonest reason by far is a misclick or a change of
   * mind — so the checkout stays exactly as it was, filled in, one press away
   * from trying again. Sending somebody to a "payment cancelled" page and
   * making them navigate back is a punishment for closing a box.
   *
   * It is still said out loud rather than passed over in silence: a window
   * that vanishes with no word leaves people wondering whether it went
   * through.
   */
  const handleDismissed = () => {
    setPaying(false);
    toast.info(
      "Payment cancelled",
      "The payment window was closed and nothing has been charged. Your details are still here - press Pay now whenever you are ready.",
    );
  };

  /**
   * Paying.
   *
   * Validated here rather than by disabling the button: a Pay that is greyed
   * out says nothing about which of eight fields is in the way, and this is
   * the last screen anyone wants to be guessing on. So it stays pressable,
   * every field is marked visited at once, and the first one that fails is
   * focused.
   *
   * Past that the order of events is fixed, and each step is somebody else's
   * job: the billing profile is recorded, our backend creates the subscription
   * and hands back its id and the *public* key; Razorpay's window takes the
   * payment; our backend checks the signature. The browser decides none of it —
   * what comes out of the window is an account of what happened, and the result
   * screen confirms it against the webhook the backend has actually seen.
   *
   * The details go up before anything else happens, and a refusal stops the
   * press. They are what the invoice will be headed with and what the tax is
   * worked out from — a payment taken against details the server never accepted
   * is a receipt nobody can raise.
   */
  const handlePay = async () => {
    const firstFailing = form.firstFailing;

    if (firstFailing) {
      // Everything at once: a form submitted whole should show everything
      // wrong with it, not the next problem one at a time.
      form.showAllErrors();
      // Not reachable from a card being read back — it is only drawn over
      // details that pass this same check, and nothing can change them behind
      // it. Opened anyway, because the alternative if that ever stops being
      // true is a page that says a field is wrong and shows no field: the
      // form goes up first, then the control it names is focused once React
      // has drawn it.
      setEditingDetails(true);
      const focusFailing = () =>
        document.getElementById(controlId(firstFailing))?.focus();
      if (readingBackDetails) {
        setTimeout(focusFailing, 0);
      } else {
        focusFailing();
      }
      toast.error(
        "Check your billing details",
        "Some of what an invoice needs is missing or does not look right.",
      );
      return;
    }

    // Recorded before a rupee moves, and on every path below — including the
    // ones that cannot take a payment at all, because the details are the
    // buyer's own and having typed them once is enough either way.
    setPaying(true);
    try {
      const stored = await form.save(form.details);
      // What came back rather than what went up: the server trims and
      // normalises, and the form should end on what the invoice will carry.
      form.reset(stored);
    } catch (cause) {
      setPaying(false);
      toast.error(
        "Could not save your billing details",
        cause instanceof Error
          ? cause.message
          : "We could not reach the server. Please try again.",
      );
      console.error("billing profile failed to save:", cause);
      return;
    }

    // Bought. The quote goes up whole rather than a plan and a figure: the
    // `expected_total` the backend checks is the `amountDue` that was actually
    // on screen, so a price that moved between the summary being drawn and this
    // being pressed is refused rather than charged. Nothing is granted here —
    // no term, no invoice, no access — until the payment settles.
    try {
      if (!priced) {
        // Not reachable: the summary is not drawn without a quote, and Pay is
        // inside it. Guarded because the order must not be made from anything
        // but the answer the buyer saw.
        throw new Error("No price to buy at.");
      }

      const order = await createPrepaidOrder(getToken, priced);

      if (!order.checkout) {
        // An order with nothing left to pay — superseded by a purchase started
        // elsewhere, or already expired. There is nothing to reopen, so the
        // buyer starts again rather than staring at a window that cannot take
        // their money.
        throw new Error(
          "This checkout is no longer open. Please pick your plan again.",
        );
      }

      const checkout = order.checkout;

      await razorpay.open({
        options: {
          // Nothing about how the window looks is sent from here. The name, the
          // mark, the colour and the row of methods are the Razorpay
          // dashboard's, read off the account this key belongs to — so what a
          // buyer sees is what was configured there, and a change made there
          // takes effect without this app shipping anything.
          key: checkout.keyId,
          order_id: checkout.orderId,
          // In paise, exactly as the backend opened the order for. Not derived
          // from the rupee total beside it: the gateway checks this against the
          // order it signed.
          amount: checkout.amountInPaise,
          currency: checkout.currency,
          // The gateway hands these back on its webhook, which is how a payment
          // made after this tab is closed still finds its order.
          notes: checkout.notes,
          description:
            checkout.description ||
            `${plan.productName} - ${planTermLabel(plan.months)}`,
          // Saves the buyer retyping what they have already given us. None of
          // it is card data; it is the same billing details on the left.
          prefill: {
            name: form.details.fullName,
            email: form.details.email,
            contact: toInternationalNumber(form.details.phone),
          },
        },
        onSuccess: async (response) => {
          // Handed straight on to the backend, which recomputes the signature
          // with the secret and reads the payment from the gateway itself.
          // Nothing here treats an unverified response as a term that has been
          // bought — and nothing is lost if this call never lands, because the
          // webhook settles the order whether or not this browser reports in.
          //
          // A refusal is not a failed purchase. The money may well be taken and
          // settling by another road, so this is logged and the result page is
          // left to ask the order what actually happened.
          try {
            await verifyPrepaidOrder(
              getToken,
              order.id,
              {
                razorpay_order_id:
                  response.razorpay_order_id ?? checkout.orderId,
                razorpay_payment_id: response.razorpay_payment_id,
                // Defaulted rather than asserted so a malformed response fails
                // the backend's check — which is where a signature is supposed
                // to fail — instead of throwing here and skipping it entirely.
                razorpay_signature: response.razorpay_signature ?? "",
              },
              plan.months,
            );
          } catch (cause) {
            console.error("prepaid verify failed:", cause);
          }

          settle(
            { state: "active" },
            {
              subscriptionId: order.id,
              paymentId: response.razorpay_payment_id,
              // What the backend made of the purchase and the day it starts,
              // off the order it froze — so the receipt heads itself correctly
              // before it has finished asking. See `StashedResult`.
              purchaseKind: order.purchaseKind,
              startsOn: order.startsOn,
              // What it was actually bought at, off the order the backend
              // froze. Carried across so the receipt quotes the charge rather
              // than re-deriving one from the price list - which it cannot do,
              // since it does not know what coupon was on it.
              amount: order.price.amountDue,
              // And the coupon, for the same reason: the receipt has no way to
              // learn what was taken off, and a payment that was discounted
              // should say so on the page that confirms it.
              couponCode: order.coupon,
              discount: order.price.discount || null,
            },
          );
        },
        onFailure: (reason) => {
          setPaying(false);
          settle(
            { state: "failed", reason },
            {
              subscriptionId: order.id,
              purchaseKind: order.purchaseKind,
              startsOn: order.startsOn,
              amount: order.price.amountDue,
              couponCode: order.coupon,
              discount: order.price.discount || null,
            },
          );
        },
        onDismiss: handleDismissed,
      });
    } catch (cause) {
      setPaying(false);
      const message =
        cause instanceof Error
          ? cause.message
          : "We could not start the payment. Please try again.";
      toast.error("Could not start the payment", message);
      console.error("prepaid purchase failed:", cause);
    }
  };

  /**
   * Putting a code on the order.
   *
   * The dialog closes only where the code actually held — a refusal leaves it
   * open with the reason printed on the row behind it, which is where somebody
   * is looking. The field's own Apply never has a dialog to close, so this is
   * one path for both.
   */
  const handleApplyCoupon = async (code: string) => {
    const applied = await quote.apply(code);
    if (applied) setBrowsingCoupons(false);
  };

  /**
   * Edit: back to the list, to pick a different term.
   *
   * One step back rather than a push, because that is what it is — this page
   * was opened from the list a moment ago, and the way back to it is the
   * browser's own. Pushing a second entry instead would leave the plans behind
   * the checkout *and* in front of it, so the hardware back button would walk
   * somebody into the checkout they had just left. Going back also returns the
   * list in the state it was left in, with the term still picked.
   *
   * A checkout opened cold — a reload, a link pasted into a new tab — has
   * nothing behind it to go back to, and `location.key` is `"default"` for
   * exactly that first entry of a session. Then it is an ordinary navigation,
   * and it names the *plans* view rather than the tab: a subscriber's Billings
   * tab opens on the plan they already have, which is the one thing Edit is
   * not asking for. See `BILLING_PLANS_PATH` — which is also what the entry
   * behind a checkout carries, so the back above lands on the list too.
   */
  const handleEdit = () => {
    if (location.key === "default") {
      // The list this checkout was opened from, which is not the same list for
      // everybody: a new practice picked its term on the trial offer and has
      // never seen the Billings tab, so sending it there would be a step
      // sideways out of the flow rather than one step back in it. Only for a
      // checkout opened cold — with an entry behind it, the browser's own back
      // lands on whichever list it actually was.
      navigate(signingUp ? ROUTES.trial : BILLING_PLANS_PATH);
      return;
    }
    navigate(-1);
  };

  /**
   * A coupon that is only good on another term.
   *
   * It changes what is being bought, which is a decision rather than a
   * re-price, so it goes back to the list of terms and lets them pick — the
   * same list Edit opens, and for the same reason: a page whose address says
   * one term while its summary prices another is the one way this screen could
   * mislead, and the list is where a term is chosen with its price in view.
   *
   * The quote starts again from whichever term they choose, and the code is
   * applied by hand from the dialog on the other side; carrying it across the
   * navigation would mean asserting a discount before the server has priced
   * it.
   *
   * Where the list *is* depends on who is buying, exactly as in `handleEdit`:
   * a practice still signing up picked its term on the trial offer and has
   * never seen the Billings tab, so sending it there would be a step sideways
   * out of the flow.
   */
  const handleSwitchPlan = () => {
    setBrowsingCoupons(false);
    navigate(signingUp ? ROUTES.trial : BILLING_PLANS_PATH);
  };

  /**
   * Who the subscription is billed to: read back where the record already
   * answers everything an invoice needs, asked for where it does not — with
   * the note that stands in when the saved details could not be read back at
   * all.
   *
   * Named rather than written into both branches below: the two layouts
   * differ in where this block sits, not in what it is. Which of the two it
   * is does not differ at all — a phone and a desktop open on the same card
   * over the same record.
   */
  const detailsBlock = readingBackDetails ? (
    <BillingDetailsSummary
      details={form.details}
      onEdit={() => setEditingDetails(true)}
      busy={paying}
    />
  ) : (
    <div>
      {form.loadFailed && (
        // Not a failure worth blocking on: these are the buyer's own details,
        // and the form works perfectly well filled in by hand. It says why it
        // is empty so nobody thinks the app has forgotten them.
        //
        // Either load failing gets the same line. To the buyer they are one
        // thing - "what you gave us before" - and which of the two records did
        // not arrive is not a distinction they can act on.
        <p className="mb-3 rounded-md border border-line bg-[#FAFBFC] px-4 py-3 text-[13px] 3xl:text-[14px] text-ink-muted">
          We could not load your saved details, so the form below is empty.
          Filling it in here works just the same.
        </p>
      )}
      <BillingDetailsForm
        details={form.details}
        onChange={form.change}
        errors={form.errors}
        touched={form.touched}
        onVisit={form.visit}
        busy={paying}
      />
    </div>
  );

  const couponRow = (
    <CouponField
      applied={quote.coupon}
      busy={quote.pricing}
      locked={paying}
      onApply={handleApplyCoupon}
      onRemove={quote.remove}
      onBrowse={() => setBrowsingCoupons(true)}
      // No caption over the box on a phone: the panel it changes is directly
      // under it and names itself, and a word over a code field at the very
      // top of a checkout is one nobody needs. "View coupons" stays, because
      // it is the only way to the list.
      hideLabel={isMobile}
    />
  );

  /** What this purchase is saving and why - the lines the phone's layout
   *  prints above its own Pay button. See `purchaseSavings`. */
  const savings = purchaseSavings(quote.coupon, price, change);

  return (
    <div className="p-page pt-page-top">
      {/* The trail back to the plans, as every screen that sits under another
          one carries it. Billing is a Settings tab rather than a screen of its
          own, so the trail starts where the rail item does and names the tab
          the way the tab bar does - `settingsCrumbs` is where both come from.

          The step this page is names what is being bought rather than
          repeating the title - and names it the way the rest of the feature
          does, `Product - Cycle`, so the crumb, the status card and the cancel
          dialog all call one plan by one name.

          Off on a phone. A checkout is the one screen in the app that is a job
          rather than a place: what is being bought is named on the panel an
          inch below, with an Edit beside it that goes back to the list, so a
          title and a two-step trail over the top spend a third of the first
          screenful saying what the next inch already says.

          Off on the way in too, at every width, and for the same reason said
          of a new practice rather than of a phone: "Settings - Billings" is
          two places they have never been, and the trail is the only thing on
          this screen that would name them. What is being bought is on the
          panel an inch below either way, with an Edit beside it that goes
          back to the offer. The same judgement as the account menu below -
          see `signingUp`. */}
      <div className="max-md:hidden">
        <PageHeader
          /* An upgrade is headed by what it is rather than by what every
             checkout is. "Complete your subscription" is right for a first
             purchase and says nothing at all to somebody who already has one
             — the question they are here to settle is which term they end up
             on, and that is the title. See `upgrade`. */
          title={change ? change.headline : "Complete your subscription"}
          breadcrumbs={
            signingUp
              ? undefined
              : [
                  ...settingsCrumbs("billings"),
                  {
                    label: `${plan.productName} - ${planTermLabel(
                      plan.months,
                    )}`,
                  },
                ]
          }
        />
      </div>

      {/* The row every screen heads itself with, minus the title: this one has
          none on a phone for the reason above. The arrow is not a title - it is
          the way out, which every screen a phone opens from somewhere carries
          at the top left, and it goes where Edit goes because that is the same
          step: back to the list this checkout was opened from, or to the list
          itself where there is no entry behind it. See `handleEdit`. Nothing
          at the other end: the account menu is the home screen's now, and a
          checkout is the last screen to offer a way out of itself. */}
      <div className="md:hidden flex items-center">
        <button
          type="button"
          onClick={handleEdit}
          aria-label="Go back"
          className="-ml-2.5 -mt-2 flex h-10 w-10 items-center justify-center rounded-full text-ink transition-colors active:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2CB]/40"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Said as soon as the request goes out, whether or not anything is drawn
          for it: the blocks that go up behind it are the shape of an answer
          rather than the answer, and are hidden from screen readers. */}
      {waiting && (
        <p role="status" className="sr-only">
          Loading your saved details...
        </p>
      )}

      {waiting ? (
        // Both halves wait on the same load, even though only the form
        // depends on it: a checkout that appears a piece at a time - a
        // finished summary, then a form under it a moment later - reads as a
        // page that broke and then repaired itself. See `CheckoutSkeleton`.
        <div className="mt-4 3xl:mt-6 grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_400px] 3xl:gap-6 4xl:lg:grid-cols-[minmax(0,1fr)_460px]">
          <CheckoutSkeleton phase={phase} />
        </div>
      ) : isMobile ? (
        /* The phone's checkout, which is a different page rather than a
           narrower one.

           What is being bought comes first and the form second. That is the
           reverse of what this screen used to do on a phone, on the reasoning
           that confirming before filling in reads backwards - and it is the
           wrong way round for this particular page. Arriving here the question
           is not "who are you" but "is this the right plan at the right
           price": the term, the coupon and the total are what somebody checks
           before they are willing to type an address at all, and under eight
           fields it meant scrolling past the whole form to find out whether
           the code they had been given held.

           The coupon row leads, because it is the one thing on the page that
           changes the figures beneath it and the two have to be read together.
           Then the panel, then who it is billed to, then what it can be paid
           with - and the button under the saving it is about to make, held
           across the foot of the screen rather than printed at the end of the
           column: a thumb is already there, and so is the total. */
        <div className="mt-4 flex flex-col gap-4">
          {couponRow}

          {/* The two terms, over the panel that prices the move between them.
              This is the only thing on a phone's checkout that names the term
              being left at all — there is no title up here to carry it. */}
          {change && <TermChangeBand change={change} />}

          <OrderSummary
            plan={plan}
            price={price}
            coupon={quote.coupon}
            change={change}
            onPay={handlePay}
            onEdit={handleEdit}
            // The "Apply" on the panel's own coupon line, for an order with
            // no code on it. The row above this panel is the other way to the
            // same list; both open it, and a code applied from either goes up
            // with the same quote.
            onBrowseCoupons={() => setBrowsingCoupons(true)}
            paying={paying}
            pricing={quote.pricing}
            variant="panel"
            // The button is pinned across the foot of the screen here rather
            // than drawn inside the panel - and the saving line goes with it.
            // See `PinnedCtaBar` at the end of this column.
            payable={false}
          />

          {detailsBlock}

          <PaymentMethods />

          {/* Pay, held across the foot of the screen rather than printed at
              the end of the page — the figure somebody came here to check and
              the button that acts on it are now on screen together, from the
              moment they arrive, and the form goes soft underneath rather than
              stopping. The classes are this page's gutter cancelled so the
              blur runs the full width and put back so the button still lines
              up with the cards, and its last padding taken off so the bar
              reaches the bottom of the screen. See `PinnedCtaBar`, which is
              also what stands the tab bar down. */}
          <PinnedCtaBar className="-mx-page px-page -mb-page">
            {/* The savings go with the button, as they do in the design: they
                are the last word on the figure printed beside "Pay". */}
            {savings.map((saving) => (
              <p
                key={saving.key}
                className="mb-2 text-center text-[13px] font-medium text-[#6234CC]"
              >
                {saving.text}
              </p>
            ))}

            <Button
              variant="primary"
              size="lg"
              fullWidth
              loading={paying}
              // Not while a re-quote is in flight: the figure on this button is
              // about to be replaced, and a payment taken against the old one is
              // the one mistake a checkout cannot make.
              disabled={quote.pricing}
              onClick={handlePay}
            >
              {/* Two spans rather than one string, so the word and the figure
                  are set apart by the button's own gap instead of by a single
                  space that runs "Pay" into the rupee sign. The accessible name
                  is unaffected: it is still read out as one phrase. */}
              <span>Pay</span>
              <span>{formatMoney(price.total)}</span>
            </Button>
          </PinnedCtaBar>
        </div>
      ) : (
        /* The form takes what is left; the summary keeps a fixed column, since
           a list of figures does not get better for being wider. */
        <div className="mt-4 3xl:mt-6 grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_400px] 3xl:gap-6 4xl:lg:grid-cols-[minmax(0,1fr)_460px]">
          {detailsBlock}

          {/* Dropped by the height of a form caption, so the summary card
              starts on the line the form's first card does. Every card on the
              left is a `FormSection`, which carries an `h-8` caption and an
              `mb-2` under it - 40px this column has nothing to fill with, its
              own heading being inside the card rather than above it. Only from
              `lg`, where the two are side by side at all; stacked, there is no
              line to share. The brand screen lines the same two up the other
              way round - see the note in `FormSection`. */}
          <div className="flex flex-col gap-4 lg:mt-10">
            {couponRow}

            {/* The two terms, between the coupon row and the figures. It sits
                over the panel rather than beside the title because the lines
                it explains are three inches below it, not up there. */}
            {change && <TermChangeBand change={change} />}

            <OrderSummary
              plan={plan}
              price={price}
              coupon={quote.coupon}
              change={change}
              onPay={handlePay}
              onEdit={handleEdit}
              // The "Apply" on the summary's own coupon line — the same list
              // "View coupons" opens, reached from the line the discount would
              // land on.
              onBrowseCoupons={() => setBrowsingCoupons(true)}
              paying={paying}
              pricing={quote.pricing}
            />

            <PaymentMethods />
          </div>
        </div>
      )}

      <AvailableCouponsModal
        open={browsingCoupons}
        onClose={() => setBrowsingCoupons(false)}
        coupons={coupons}
        applied={quote.coupon}
        cycle={plan.id}
        plans={priceList.plans}
        onApply={handleApplyCoupon}
        onRemove={quote.remove}
        onSwitchPlan={handleSwitchPlan}
        busy={quote.pricing}
      />
    </div>
  );
};

export default BillingCheckoutPage;
